import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { barGradient, summaryState } from "./ganttColors";

const DAY_LETTER = ["D", "L", "M", "M", "G", "V", "S"];
const MONTHS = ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"];
const BAR_CLASS = { done: "done", active: "studio", soon: "soon", late: "late", waiting: "waiting", planned: "planned" };
const it = value => String(value || "").slice(0, 10).split("-").reverse().join("/");
const day = value => new Date(`${String(value).slice(0, 10)}T00:00:00`);
const addDays = (date, amount) => { const next = new Date(date); next.setDate(next.getDate() + amount); return next; };
const iso = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const diff = (from, to) => Math.round((day(to) - from) / 86400000);

const initials = name => (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase() || "—";
const avatarTone = name => ["a", "b", "c", "d", "e"][(name || "").length % 5];

function useScale(window) {
  return useMemo(() => {
    const start = day(window.start || iso(new Date()));
    const total = Math.max(diff(start, window.end || iso(new Date())) + 1, 7);
    // Meno è meglio: settimane fino a 3 mesi, mesi sugli orizzonti lunghi.
    const detail = total <= 50 ? "week" : total <= 130 ? "week" : "month";
    const perDay = total <= 50 ? 19 : total <= 130 ? 8.2 : total <= 220 ? 4.6 : 2.5;
    const groups = [];
    let cursor = 0;
    while (cursor < total) {
      const date = addDays(start, cursor);
      if (detail === "week") {
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        const span = Math.max(1, Math.min(endOfMonth - date.getDate() + 1, total - cursor));
        groups.push({ key: iso(date), label: `${MONTHS[date.getMonth()]} ${String(date.getFullYear()).slice(2)}`, width: span * perDay });
        cursor += span;
      } else {
        const endOfQuarter = new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3 + 3, 0);
        const span = Math.max(1, Math.min(diff(date, iso(endOfQuarter)) + 1, total - cursor));
        groups.push({ key: iso(date), label: `${Math.floor(date.getMonth() / 3) + 1}° TRIM ${String(date.getFullYear()).slice(2)}`, width: span * perDay });
        cursor += span;
      }
    }
    const ticks = [];
    cursor = 0;
    while (cursor < total) {
      const date = addDays(start, cursor);
      if (detail === "week") {
        const span = Math.max(1, Math.min(7 - ((date.getDay() + 6) % 7), total - cursor));
        ticks.push({ key: iso(date), label: `${date.getDate()}`, width: span * perDay, wide: span * perDay > 26 });
        cursor += span;
      } else {
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        const span = Math.max(1, Math.min(endOfMonth - date.getDate() + 1, total - cursor));
        ticks.push({ key: iso(date), label: MONTHS[date.getMonth()], width: span * perDay, wide: span * perDay > 26 });
        cursor += span;
      }
    }
    const cells = [];
    for (let index = 0; index < total; index += 1) {
      const date = addDays(start, index);
      const weekend = [0, 6].includes(date.getDay());
      cells.push({ index, iso: iso(date), weekend: weekend && perDay >= 12, label: `${DAY_LETTER[date.getDay()]}${date.getDate()}`, monday: date.getDay() === 1 });
    }
    return { start, total, detail, perDay, groups, ticks, cells, width: total * perDay,
      x: value => Math.max(0, Math.min(total, diff(start, value))) * perDay };
  }, [window.start, window.end]);
}

const Bar = ({ phase, scale, row, onDetail, summary }) => {
  if (diff(scale.start, phase.end) < 0 || diff(scale.start, phase.start) >= scale.total) return null;
  const left = scale.x(phase.start);
  const width = Math.max(scale.x(phase.end) - left, 6);
  const state = summary ? summaryState(row) : BAR_CLASS[phase.state] || "planned";
  const statusLabel = { studio: "In carico allo studio", waiting: `Attesa ${row.party || phase.party || "esterna"}`, done: "Completata", settled: "Saldata", late: "Studio in ritardo", soon: "In scadenza", planned: "Programmata" }[state];
  return <button className={`gx-bar ${summary ? "summary " : ""}${state}`}
    style={{ left, width, ...barGradient(state) }} data-testid={summary ? `gantt-summary-${row.id}` : `gantt-phase-${row.id}-${phase.key}`}
    data-gantt-state={state} aria-label={`${summary ? row.name : phase.label} · ${statusLabel}`}
    title={summary ? `${row.name} · ${statusLabel} · avanzamento ${row.progress}%`
      : `${phase.label} · ${phase.owner === "studio" ? "attività dello studio" : `attesa ${phase.party || "esterna"}`} · ${it(phase.start)} → ${it(phase.end)}${phase.deadline ? ` · ${phase.deadline_label || "Termine"} ${it(phase.deadline)}` : ""}`}
    onClick={event => { event.stopPropagation(); onDetail(row, summary ? null : phase); }} />;
};

export const GanttChart = ({ data, selectedId, onSelect, onDetail }) => {
  const [expanded, setExpanded] = useState({});
  const scale = useScale(data?.window || {});
  if (!data?.rows?.length) return <p className="gantt-empty" data-testid="gantt-empty">Nessuna pratica da programmare con questi filtri.</p>;
  const inside = value => { const text = String(value || "").slice(0, 10); return text && text >= data.window.start && text <= data.window.end; };
  const todayX = scale.x(data.today);
  const summaryTitle = row => {
    const next = row.milestones.find(item => !item.done && item.date >= data.today);
    return [`${row.name}${row.client_name ? ` · ${row.client_name}` : ""}`,
      `Avanzamento ${row.progress}% · ${row.status}`,
      `Prossima azione: ${row.next_action.text}${row.next_action.due ? ` entro ${it(row.next_action.due)}` : ""}`,
      `Responsabile: ${row.responsible || "da assegnare"} · ${[row.has_studio_actions && "azioni dello studio", row.has_external_waits && `attesa ${[...new Set(row.external_waits.map(w => w.party))].join(", ")}`].filter(Boolean).join(" + ") || "nessuna azione aperta"}`,
      row.nearest_date ? `Scadenza: ${row.nearest_label} ${it(row.nearest_date)} (${row.nearest_days} gg)` : "Nessuna scadenza calcolata",
      `Da fatturare ${row.economics.to_invoice.toLocaleString("it-IT")} € · da incassare ${row.economics.outstanding.toLocaleString("it-IT")} €`,
      next ? `Prossima milestone: ${next.label} ${it(next.date)}` : ""].filter(Boolean).join("\n");
  };

  const marks = (row, list, rules, suffix = "") => <>
    {rules.filter(rule => inside(rule.date)).map(rule => <span key={`r-${rule.label}`} className="gx-rule"
      style={{ left: scale.x(rule.date) }} data-testid={`gantt-rule-${row.id}`}
      title={`${rule.label}: ${it(rule.date)} — termine calcolato automaticamente in base alla tipologia della pratica`} />)}
    {list.filter(item => inside(item.date)).map(item => <button key={item.key} type="button"
      className={`gx-diamond ${item.kind === "economica" ? "euro" : item.kind === "catasto" ? "catasto" : ""} ${item.done ? "" : "open"}`}
      style={{ left: scale.x(item.date) + list.filter(other => other.date === item.date).findIndex(other => other.key === item.key) * 9 }} data-testid={`gantt-milestone-${row.id}-${item.key}${suffix}`}
      aria-label={`${item.label} · ${it(item.date)}`}
      title={`${item.label} · ${it(item.date)}${item.esito ? ` · ${item.esito}` : ""}${item.protocollo ? ` · ${item.protocollo}` : ""}${item.amount ? ` · ${item.amount.toLocaleString("it-IT")} € ${item.amount_label || "maturati"}` : ""}${item.notes ? ` · ${item.notes}` : ""}`}
      onClick={event => { event.stopPropagation(); onDetail(row, { ...item, isMilestone: true }); }} />)}
  </>;

  return <>
    <div className="gx" data-testid="gantt-scroll">
      <div className="gx-side">
        <div className="gx-side-head"><span>Pratica</span><span>Avanz.</span></div>
        {data.rows.map(row => {
          const open = !!expanded[row.id];
          const late = row.phases.some(phase => phase.state === "late");
          return <React.Fragment key={row.id}>
            <div className={`gx-side-row parent ${row.has_external_waits ? "has-waits" : ""} ${selectedId === row.id ? "on" : ""}`} data-testid={`gantt-row-${row.id}`}
              onClick={() => onSelect(row)} title={summaryTitle(row)}>
              <button className="gx-caret" aria-expanded={open} aria-label={`${open ? "Comprimi" : "Espandi"} ${row.name}`} data-testid={`gantt-toggle-${row.id}`}
                onClick={event => { event.stopPropagation(); setExpanded({ ...expanded, [row.id]: !open }); }}>
                {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              </button>
              <span className="gx-practice-label"><span className="gx-name" data-testid={`gantt-practice-name-${row.id}`}>{row.name}</span><span className="gx-row-badges">
              {row.catasto_attivo && <span className="gx-tag catasto" title={row.stato_catasto} data-testid={`gantt-catasto-${row.id}`}>CATASTO</span>}
              {row.economics?.stage === "saldata" ? <span className="gx-tag done" data-testid={`gantt-status-${row.id}`}>Saldata</span> : row.closed && !row.has_studio_actions && !row.has_external_waits ? <span className="gx-tag done" data-testid={`gantt-status-${row.id}`}>Completata</span> : null}
              {row.has_studio_actions && <span className={`gx-tag ${late ? "late" : "studio"}`} data-testid={`gantt-studio-${row.id}`}>{late ? "Ritardo studio" : "Studio"}</span>}
              {row.has_external_waits && <span className="gx-tag esterno" data-testid={`gantt-waiting-${row.id}`}>Attesa</span>}
              {!!row.economics.to_invoice && <span className="gx-tag euro" data-testid={`gantt-to-invoice-${row.id}`} title={`${row.economics.to_invoice.toLocaleString("it-IT")} € maturati da fatturare`}>€</span>}
              </span><small className={`gx-client ${row.client_name ? "" : "missing"}`} data-testid={`gantt-client-${row.id}`}>{[row.catasto_attivo ? "Catasto" : row.type_label === "Da definire" ? "" : row.type_label, row.client_name || "Committente da definire"].filter(Boolean).join(" · ")}</small>{row.has_external_waits && <small className="gx-wait-summary" data-testid={`gantt-wait-summary-${row.id}`}>{row.external_waits.map(w => `${w.party} · ${w.action}${w.waiting_days == null ? " · data da indicare" : ` · ${w.waiting_days} gg`}`).join(" / ")}</small>}</span>
              <span className="gx-pct" data-testid={`gantt-progress-${row.id}`}>{row.progress}%</span>
              <span className={`gx-avatar ${avatarTone(row.responsible)}`} title={`Responsabile: ${row.responsible || "da assegnare"}`}>{initials(row.responsible)}</span>
            </div>
            {open && row.phases.map(phase => <div className="gx-side-row child" key={`${row.id}-${phase.key}`} data-testid={`gantt-phase-label-${row.id}-${phase.key}`}
              onClick={() => onDetail(row, phase)}>
              <span className={`gx-dot ${BAR_CLASS[phase.state] || "planned"}`} />
              <span className="gx-name">{phase.label}</span>
              <span className="gx-owner">{phase.owner === "studio" ? "Studio" : phase.party || "Esterno"}</span>
            </div>)}
            {open && row.milestones.filter(m => m.source === "catasto" && inside(m.date)).map(m => <button type="button" className="gx-side-row child catasto-track-label" key={m.key} data-testid={`gantt-catasto-label-${row.id}-${m.key}`} onClick={() => onDetail(row, { ...m, isMilestone: true })}><span className="gx-name">{m.label}</span><span className="gx-owner">{it(m.date)}</span></button>)}
          </React.Fragment>;
        })}
      </div>

      <div className="gx-main">
        <div className="gx-head" style={{ width: scale.width }}>
          <div className="gx-head-groups">
            {scale.groups.map(group => <span key={group.key} style={{ width: group.width }}>{group.label}</span>)}
          </div>
          <div className="gx-head-days">
            {scale.ticks.map(tick => <span key={tick.key} style={{ width: tick.width }}>{tick.wide ? tick.label : ""}</span>)}
          </div>
          {inside(data.today) && <span className="gx-today-pill" style={{ left: todayX }}>OGGI</span>}
        </div>
        <div className="gx-body" style={{ width: scale.width }}>
          <div className="gx-grid">
            {scale.cells.map(cell => (cell.weekend || cell.monday) && <span key={cell.iso}
              className={`${cell.weekend ? "we" : "line"}`} style={{ left: cell.index * scale.perDay, width: scale.perDay }} />)}
          </div>
          {inside(data.today) && <span className="gx-today" style={{ left: todayX }} />}
          {data.rows.map(row => {
            const open = !!expanded[row.id];
            const starts = row.phases.map(p => p.start).sort();
            const ends = row.phases.map(p => p.end).sort();
            const span = { start: starts[0] || data.today, end: ends[ends.length - 1] || data.today };
            return <React.Fragment key={row.id}>
              <div className={`gx-row parent ${row.has_external_waits ? "has-waits" : ""} ${selectedId === row.id ? "on" : ""}`} data-testid={`gantt-timeline-row-${row.id}`} onClick={() => onSelect(row)} title={summaryTitle(row)}>
                <Bar phase={span} scale={scale} row={row} onDetail={onDetail} summary />
                {marks(row, row.milestones, row.rules)}
              </div>
              {open && row.phases.map(phase => <div className="gx-row child" key={`${row.id}-${phase.key}`} data-testid={`gantt-phase-track-${row.id}-${phase.key}`}>
                <Bar phase={phase} scale={scale} row={row} onDetail={onDetail} />
                {phase.deadline && inside(phase.deadline) && <span className="gx-rule soft" style={{ left: scale.x(phase.deadline) }}
                  title={`${phase.deadline_label || "Termine"}: ${it(phase.deadline)}`} />}
              </div>)}
              {open && row.milestones.filter(m => m.source === "catasto" && inside(m.date)).map(m => <div className="gx-row child" key={m.key}>{marks(row, [m], [], "-expanded")}</div>)}
            </React.Fragment>;
          })}
        </div>
      </div>
    </div>
    <div className="gantt-legend" data-testid="gantt-legend">
      <span><i className="studio" />Studio</span>
      <span><i className="waiting" />Attesa esterna</span>
      <span><i className="late" />Ritardo studio</span>
      <span><i className="soon" />In scadenza</span>
      <span><i className="done" />Completata</span>
      <span><i className="diamond" />Milestone</span>
      <span data-testid="gantt-catasto-legend"><i className="catasto" />Catasto</span>
      <span><i className="euro" />Quota maturata</span>
      <span><i className="rule" />Termine normativo</span>
    </div>
  </>;
};
